import { jsx as _jsx } from "react/jsx-runtime";
import { cn } from '../cn.js';
/**
 * A person, as a circle with their initials in it.
 *
 * Every list of people in the reference leads with one, and it is doing real
 * work: a coordinator scanning a roster recognises the row before they have
 * read the name. What makes that possible is the colour being **stable** — the
 * same person is the same colour on the schedule, on the roster, in the drawer,
 * tomorrow, and on their colleague's screen.
 *
 * So the fill is a hash of the person's id rather than their position in a
 * list. Sorting a table must not repaint it.
 *
 * ## The initials are text
 *
 * Not decoration, which is why the ring is six saturated fills rather than six
 * pastels — every one carries white at 4.5:1 and `contrast.spec.ts` asserts it.
 * The circle is `aria-hidden` and the name is rendered beside it, because
 * "MO" announced before "Miriam Okafor" is the same information twice and the
 * first time is worse.
 */
const RING = 6;
/** `bg-avatar-1`…`bg-avatar-6`, written out — Tailwind reads source text. */
const FILL = [
    'bg-avatar-1',
    'bg-avatar-2',
    'bg-avatar-3',
    'bg-avatar-4',
    'bg-avatar-5',
    'bg-avatar-6',
];
/**
 * Which of the six a seed lands on.
 *
 * FNV-1a, because it is four lines and has no dependency. Any hash would do;
 * what matters is that it is a pure function of the id, so the answer does not
 * depend on what else is on screen.
 */
export function avatarIndex(seed) {
    let hash = 0x811c9dc5;
    for (let index = 0; index < seed.length; index += 1) {
        hash ^= seed.charCodeAt(index);
        hash = Math.imul(hash, 0x01000193) >>> 0;
    }
    return hash % RING;
}
/**
 * One or two letters, from however many names a person has.
 *
 * First and last, not first-two-words: "Mary Anne Okafor" is MO, which is what
 * her colleagues would write. Falls back to the first two characters of
 * whatever it was given, so a single-word display name still renders something
 * rather than an empty circle.
 */
export function initials(name) {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0)
        return '?';
    if (parts.length === 1)
        return (parts[0] ?? '').slice(0, 2).toUpperCase();
    const first = parts[0]?.[0] ?? '';
    const last = parts[parts.length - 1]?.[0] ?? '';
    return `${first}${last}`.toUpperCase();
}
const SIZE = {
    sm: 'h-avatar-sm w-avatar-sm text-xs',
    md: 'h-avatar w-avatar text-sm',
};
export function Avatar({ name, 
/** The stable identifier. Falls back to the name, which is stable enough. */
seed, size = 'md', className, }) {
    const fill = FILL[avatarIndex(seed ?? name)] ?? FILL[0];
    return (_jsx("span", { "aria-hidden": "true", "data-avatar": name, className: cn('text-ink-inv grid flex-none place-items-center rounded-full font-semibold', SIZE[size], fill, className), children: initials(name) }));
}
//# sourceMappingURL=avatar.js.map